import dayjs from 'dayjs';
import {
  DataModel, DataModelBuilder, SerializationContext, SerializedData,
} from '../../dto/data-model.js';
import { MapUtils } from '../../../utility/map-utils.js';
import {
  cloneData, cloneDataOrNull, cloneListOrNull, generateUniqueDatabaseId,
  isSameData, isSameList,
} from '../../dto/data-model-utils.js';
import { Product } from '../product.js';
import { TrueTime } from '../../../time/true-time.js';
import { TimeZone } from '../../../time/timezone.js';
import { BusinessModuleRef } from '../business-module-ref.js';
import { SellableVoucher, VoucherStatus } from './voucher.js';
import { ComponentList } from '../../../components/core/component-list.js';
import { fromEnumAsString, toEnumFromString } from '../../../serialization/enum-utils.js';
import { isNotNullOrUndefined, isNullOrUndefined } from '../../../utility/null-safety.js';
import { Price } from '../price.js';
import { TicketGiftCardComponent } from '../../../components/voucher/ticket-gift-card-component.js';
import { Discount, DiscountType } from './discount.js';
import { VoucherAttachmentComponent } from '../../../components/voucher/voucher-attachment-component.js';
import { VersionRange } from '../../core/version-range.js';
import { JSONUtils } from '../../../utility/json-utils.js';
import { Result } from '../../../utility/result.js';
import { ErrorLike } from '../../core/error-like.js';
import { IllegalOperationException } from '../../../exceptions/illegal-operation-exception.js';
import { CustomerOrderBase } from '../../../contracts/customer-order.js';

/**
 * @deprecated Use GiftCardTemplate and GiftCard for new gift card models.
 */
export class TicketGiftCard extends SellableVoucher {
  public static readonly TYPE_ID: string = '14397d41116c4d9683bb03074ea11643';
  public static readonly SCHEMA_VERSION: number = 8;

  private _status: VoucherStatus;
  private _includedEvents: string[] | null;
  private _excludedEvents: string[] | null;
  private _usedAt: dayjs.Dayjs | null;
  private _usedValue: Price | null;

  private constructor(
    public readonly uniqueId: string,
    public readonly businessRef: BusinessModuleRef,
    public readonly creationDate: dayjs.Dayjs,
    public readonly orderId: string,
    public readonly customerId: string,
    public readonly productId: string,
    public readonly productPrice: Price,
    public readonly value: Price,
    public readonly components: ComponentList,
    public readonly startDate: dayjs.Dayjs | null,
    public readonly expirationDate: dayjs.Dayjs | null,
    public readonly serialNumber: string | null,
    public readonly userId: string | null,
    public readonly isGuestOrder: boolean | null,
    public readonly orderEntryId: string | null,
    includedEvents: string[] | null,
    excludedEvents: string[] | null,
    status: VoucherStatus,
    usedAt: dayjs.Dayjs | null,
    usedValue: Price | null,
  ) {
    super();
    this._status = status;
    this._usedAt = usedAt;
    this._usedValue = usedValue;
    this._includedEvents = includedEvents;
    this._excludedEvents = excludedEvents;
  }

  get $typeId(): string {
    return TicketGiftCard.TYPE_ID;
  }

  get code(): string {
    return this.uniqueId;
  }

  get status(): VoucherStatus {
    return this._status;
  }

  get usedAt(): dayjs.Dayjs | null {
    return this._usedAt;
  }

  get usedValue(): Price | null {
    return this._usedValue;
  }

  get remainingValue(): Price {
    return isNotNullOrUndefined(this._usedValue) ?
        this.value.difference(this._usedValue) :
        this.value;
  }

  get includedEvents(): string[] | null {
    return this._includedEvents;
  }

  get excludedEvents(): string[] | null {
    return this._excludedEvents;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(5, TicketGiftCard.SCHEMA_VERSION);
  }

  static create(args: {
    uniqueId: string,
    businessRef: BusinessModuleRef,
    creationDate: dayjs.Dayjs,
    orderId: string,
    customerId: string,
    productId: string,
    productPrice: Price,
    value: Price,
    components: ComponentList,
    startDate: dayjs.Dayjs | null,
    expirationDate: dayjs.Dayjs | null,
    includedEvents: string[] | null,
    excludedEvents: string[] | null,
    status: VoucherStatus,
    usedAt: dayjs.Dayjs | null,
    usedValue: Price | null,
    serialNumber: string | null,
    userId: string | null,
    isGuestOrder: boolean | null,
    orderEntryId: string | null,
  }): TicketGiftCard {
    return new TicketGiftCard(
        args.uniqueId,
        args.businessRef,
        args.creationDate,
        args.orderId,
        args.customerId,
        args.productId,
        args.productPrice,
        args.value,
        args.components,
        args.startDate,
        args.expirationDate,
        args.serialNumber,
        args.userId,
        args.isGuestOrder,
        args.orderEntryId,
        args.includedEvents,
        args.excludedEvents,
        args.status,
        args.usedAt,
        args.usedValue,
    );
  }

  static fromProductOfOrder(
      order: CustomerOrderBase,
      product: Product,
      options: { orderEntryId: string | null },
  ): TicketGiftCard | null {
    const component = product.findComponentByTypeId<TicketGiftCardComponent>(TicketGiftCardComponent.TYPE_ID);
    if(isNullOrUndefined(component) || isNullOrUndefined(order.customerId)) {
      return null;
    }

    const componentList = new ComponentList([]);

    const attachmentComponent = product.findComponentByTypeId(
        VoucherAttachmentComponent.TYPE_ID,
    );

    if(isNotNullOrUndefined(attachmentComponent)) {
      componentList.addComponent(attachmentComponent.clone());
    }

    return this.create({
      uniqueId: TicketGiftCard.generatePromoCode(),
      businessRef: order.businessRef.clone(),
      creationDate: TrueTime.now(),
      orderId: order.uniqueId,
      customerId: order.customerId,
      productId: product.uniqueId,
      productPrice: product.price.clone(),
      value: component.value.clone(),
      components: componentList,
      startDate: component.startDate,
      expirationDate: component.expirationDate,
      includedEvents: cloneListOrNull(component.includedEvents),
      excludedEvents: cloneListOrNull(component.excludedEvents),
      status: VoucherStatus.Live,
      usedAt: null,
      usedValue: null,
      serialNumber: null,
      userId: order.userId,
      isGuestOrder: order.userId != null ? order.isGuestOrder : null,
      orderEntryId: options.orderEntryId,
    });
  }

  static fromMap(context: SerializationContext, map: SerializedData): TicketGiftCard {
    return this.create({
      uniqueId: MapUtils.getString(map, 'uniqueId'),
      businessRef: BusinessModuleRef.mustDecode(MapUtils.getString(map, 'businessRef')),
      creationDate: TimeZone.decodeDate(MapUtils.get<number>(map, 'creationDate')),
      orderId: MapUtils.getString(map, 'orderId'),
      customerId: MapUtils.getString(map, 'customerId'),
      productId: MapUtils.getString(map, 'productId'),
      productPrice: Price.decode(MapUtils.getString(map, 'productPrice')),
      value: Price.decode(MapUtils.getString(map, 'value')),
      components: ComponentList.deserialize(
          context, MapUtils.getList<SerializedData>(map, 'components', (data) => JSONUtils.decodeRecord(data)),
      ),
      startDate: TimeZone.decodeDateOrNull(MapUtils.getNumberOrNull(map, 'startDate')),
      expirationDate: TimeZone.decodeDateOrNull(MapUtils.getNumberOrNull(map, 'expirationDate')),
      serialNumber: MapUtils.getStringOrNull(map, 'serialNumber'),
      userId: MapUtils.getStringOrNull(map, 'userId'),
      isGuestOrder: MapUtils.getBooleanOrNull(map, 'isGuestOrder'),
      orderEntryId: MapUtils.getStringOrNull(map, 'orderEntryId'),
      includedEvents: MapUtils.getListOrNull<string>(map, 'includedEvents', (e) => JSONUtils.decodeString(e)),
      excludedEvents: MapUtils.getListOrNull<string>(map, 'excludedEvents', (e) => JSONUtils.decodeString(e)),
      status: toEnumFromString(MapUtils.get<string>(map, '$status'), VoucherStatus),
      usedAt: TimeZone.decodeDateOrNull(MapUtils.getNumberOrNull(map, 'usedAt')),
      usedValue: Price.decodeOrNull(MapUtils.getStringOrNull(map, 'usedValue')),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'code': this.code,
      'businessRef': this.businessRef.encode(),
      'creationDate': TimeZone.encodeDate(this.creationDate),
      'orderId': this.orderId,
      'customerId': this.customerId,
      'productId': this.productId,
      'productPrice': this.productPrice.encode(),
      'startDate': TimeZone.encodeDateOrNull(this.startDate),
      'expirationDate': TimeZone.encodeDateOrNull(this.expirationDate),
      'serialNumber': this.serialNumber,
      'userId': this.userId,
      'isGuestOrder': this.isGuestOrder,
      'orderEntryId': this.orderEntryId,
      '$status': fromEnumAsString(this.status),
      'usedAt': TimeZone.encodeDateOrNull(this.usedAt),
      'includedEvents': this.includedEvents,
      'excludedEvents': this.excludedEvents,
      'value': this.value.encode(),
      'usedValue': this.usedValue?.encode() ?? null,
      'components': this.components.serialize(context),
    };
  }

  clone(): this {
    return TicketGiftCard.create({
      uniqueId: this.uniqueId,
      businessRef: this.businessRef.clone(),
      creationDate: TimeZone.cloneDate(this.creationDate),
      orderId: this.orderId,
      customerId: this.customerId,
      productId: this.productId,
      productPrice: this.productPrice.clone(),
      value: this.value.clone(),
      components: this.components.clone(),
      startDate: TimeZone.cloneDateOrNull(this.startDate),
      expirationDate: TimeZone.cloneDateOrNull(this.expirationDate),
      serialNumber: this.serialNumber,
      userId: this.userId,
      isGuestOrder: this.isGuestOrder,
      orderEntryId: this.orderEntryId,
      includedEvents: cloneListOrNull(this.includedEvents),
      excludedEvents: cloneListOrNull(this.excludedEvents),
      status: this.status,
      usedAt: TimeZone.cloneDateOrNull(this.usedAt),
      usedValue: this.usedValue?.clone() ?? null,
    }) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
        isSameData(other.businessRef, this.businessRef) &&
        other.creationDate === this.creationDate &&
        other.orderId === this.orderId &&
        other.customerId === this.customerId &&
        other.productId === this.productId &&
        other.status === this.status &&
        other.usedAt === this.usedAt &&
        other.startDate === this.startDate &&
        other.expirationDate === this.expirationDate &&
        other.serialNumber === this.serialNumber &&
        other.userId === this.userId &&
        other.isGuestOrder === this.isGuestOrder &&
        other.orderEntryId === this.orderEntryId &&
        isSameList(other.includedEvents, this.includedEvents) &&
        isSameList(other.excludedEvents, this.excludedEvents) &&
        isSameData(other.value, this.value) &&
        isSameData(other.productPrice, this.productPrice) &&
        isSameData(other.usedValue, this.usedValue) &&
        other.components.isSame(this.components);
  }

  copyWith({
    uniqueId = this.uniqueId,
    businessRef = cloneData(this.businessRef),
    creationDate = TimeZone.cloneDate(this.creationDate),
    orderId = this.orderId,
    customerId = this.customerId,
    productId = this.productId,
    productPrice = cloneData(this.productPrice),
    value = cloneData(this.value),
    components = cloneData(this.components),
    startDate = TimeZone.cloneDateOrNull(this.startDate),
    expirationDate = TimeZone.cloneDateOrNull(this.expirationDate),
    serialNumber = this.serialNumber,
    userId = this.userId,
    isGuestOrder = this.isGuestOrder,
    orderEntryId = this.orderEntryId,
    includedEvents = cloneListOrNull(this.includedEvents),
    excludedEvents = cloneListOrNull(this.excludedEvents),
    status = this.status,
    usedAt = TimeZone.cloneDateOrNull(this.usedAt),
    usedValue = cloneDataOrNull(this.usedValue),
  }: {
    uniqueId?: string,
    businessRef?: BusinessModuleRef,
    creationDate?: dayjs.Dayjs,
    orderId?: string,
    customerId?: string,
    productId?: string,
    productPrice?: Price,
    value?: Price,
    components?: ComponentList,
    startDate?: dayjs.Dayjs | null,
    expirationDate?: dayjs.Dayjs | null,
    serialNumber?: string | null,
    userId?: string | null,
    isGuestOrder?: boolean | null,
    orderEntryId?: string | null,
    includedEvents?: string[] | null,
    excludedEvents?: string[] | null,
    status?: VoucherStatus,
    usedAt?: dayjs.Dayjs | null,
    usedValue?: Price | null,
  } = {}): this {
    return TicketGiftCard.create({
      uniqueId,
      businessRef,
      creationDate,
      orderId,
      customerId,
      productId,
      productPrice,
      value,
      components,
      startDate,
      expirationDate,
      includedEvents,
      excludedEvents,
      status,
      usedAt,
      usedValue,
      serialNumber,
      userId,
      isGuestOrder,
      orderEntryId,
    }) as this;
  }

  copyAsCanceled(isRefund: boolean): this {
    return this.copyWith({
      status: isRefund ? VoucherStatus.Refunded : VoucherStatus.Canceled,
    });
  }

  updateUsage(discount: Discount, now?: dayjs.Dayjs): void {
    if(discount.type !== DiscountType.Fixed || isNullOrUndefined(discount.fixedPrice)) {
      return;
    }
    if(this.status !== VoucherStatus.Live) {
      return;
    }

    if(isNullOrUndefined(this._usedValue)) {
      this._usedValue = Price.min(this.value, discount.fixedPrice);
    }
    else {
      this._usedValue = Price.min(this.value, this._usedValue.add(discount.fixedPrice));
    }

    if(this._usedValue.compareTo(this.value) >= 0) {
      this._usedAt = now ?? TrueTime.now();
      this._status = VoucherStatus.Used;
    }
  }

  tryUpdateUsage(discount: Discount, now: dayjs.Dayjs): Result<this, ErrorLike> {
    const self = this.clone();

    if(discount.type !== DiscountType.Fixed) {
      return Result.error(new Error(`Discount type {${discount.type}} is not ${DiscountType.Fixed}.`));
    }

    if(discount.fixedPrice == null) {
      return Result.error(new Error(`Discount fixed price {${discount.fixedPrice}} is invalid.`));
    }

    if(self.status !== VoucherStatus.Live) {
      return Result.error(new Error(`Voucher status {${self.status}} is not ${VoucherStatus.Live}.`));
    }

    if(self.remainingValue.compareTo(discount.fixedPrice) < 0) {
      return Result.error(new Error(`Remaining value {${self.remainingValue.format()}} is less than discount fixed price {${discount.fixedPrice.format()}}.`));
    }

    self._usedValue ??= Price.zero(discount.fixedPrice.currency);
    self._usedValue = self._usedValue.add(discount.fixedPrice);

    if(self._usedValue.compareTo(self.value) >= 0) {
      self._usedAt = now;
      self._status = VoucherStatus.Used;
    }

    return Result.ok(self);
  }

  includeEvents(events: string[]): void {
    if(this._includedEvents == null) {
      this._includedEvents = [...events];
    }
    else {
      for(const eventId of events) {
        if(!this._includedEvents.includes(eventId)) {
          this._includedEvents.push(eventId);
        }
      }
    }
  }

  excludeEvents(events: string[]): void {
    if(this._excludedEvents == null) {
      this._excludedEvents = [...events];
    }
    else {
      for(const eventId of events) {
        if(!this._excludedEvents.includes(eventId)) {
          this._excludedEvents.push(eventId);
        }
      }
    }
  }

  setSerialNumber(serialNumber: string): this {
    if(this.serialNumber != null) {
      throw new IllegalOperationException(`Cannot set serial number {${serialNumber}} for Ticket Gift Card {${this.uniqueId}} of business {${this.businessRef}} because it already has a serial number {${this.serialNumber}}.`);
    }

    return this.copyWith({
      serialNumber: serialNumber,
    });
  }

  static generatePromoCode(): string {
    const randomStr = generateUniqueDatabaseId({ length: 15, alphabet: 'NONSIMILAR_CHARACTERS' });
    return `GC-${randomStr.substring(0, 5)}-${randomStr.substring(5, 10)}-${randomStr.substring(10, randomStr.length)}`;
  }

  static verifyType(dm: DataModel): boolean {
    return dm instanceof TicketGiftCard;
  }

  static assertType(dm: DataModel): dm is TicketGiftCard {
    return dm instanceof TicketGiftCard;
  }
}

export class TicketGiftCardBuilder extends DataModelBuilder<TicketGiftCard> {
  build(context: SerializationContext, map: SerializedData): TicketGiftCard {
    return TicketGiftCard.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(TicketGiftCard.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(TicketGiftCard.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version we added `productPrice` of type `Price`.
    if(version < 2) {
      map['productPrice'] = MapUtils.get<string>(map, 'value');
      version = 2;
    }

    /**
     * Version 3:
     *  - parameter `startDate` type {@link dayjs.Dayjs | null} introduced.
     */
    if(version < 3) version = 3;

    /**
     * Version 4:
     * - add `serialNumber` field of type {@link string | null}.
     */
    if(version < 4) version = 4;

    if(version < 5) {
      map['includedEvents'] = map['eventId'];
      version = 5;
    }

    if(version < 6) version = 6;

    /**
     * Version 7:
     *  - parameter `userId` type {string | null} was introduced.
     *  - parameter `isGuestOrder` type {boolean | null} was introduced.
     */
    if(version < 7) version = 7;

    /**
     * Version 8:
     *  - parameter `orderEntryId` type {string | null} was introduced.
     */
    if(version < 8) version = 8;

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
